if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handlePrevClick = this.showPreviousMedia.bind(this);
        this.handleNextClick = this.showNextMedia.bind(this);
        this.touchStartX = null;
        this.touchStartY = null;
        this.motionTimeout = null;
      }

      hide() {
        this.removeModalEvents();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.setElements();
        this.addModalEvents();
        this.showActiveMedia();
      }

      setElements() {
        this.elements = {
          container: this.querySelector('[role="document"]'),
          previousButton: this.querySelector('[data-product-modal-prev]'),
          nextButton: this.querySelector('[data-product-modal-next]'),
        };
        this.mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
      }

      addModalEvents() {
        if (this.eventsBound || !this.elements?.container) return;
        this.eventsBound = true;

        this.elements.container.addEventListener('pointerup', this.handlePointerUp);
        this.elements.container.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.elements.container.addEventListener('touchend', this.handleTouchEnd, { passive: true });
        this.addEventListener('keydown', this.handleKeydown);

        [this.elements.previousButton, this.elements.nextButton].forEach((button) => {
          if (!button) return;
          button.addEventListener('pointerup', this.handlePointerUp);
        });

        this.elements.previousButton?.addEventListener('click', this.handlePrevClick);
        this.elements.nextButton?.addEventListener('click', this.handleNextClick);
      }

      removeModalEvents() {
        if (!this.eventsBound || !this.elements?.container) return;
        this.eventsBound = false;

        this.elements.container.removeEventListener('pointerup', this.handlePointerUp);
        this.elements.container.removeEventListener('touchstart', this.handleTouchStart);
        this.elements.container.removeEventListener('touchend', this.handleTouchEnd);
        this.removeEventListener('keydown', this.handleKeydown);

        [this.elements.previousButton, this.elements.nextButton].forEach((button) => {
          if (!button) return;
          button.removeEventListener('pointerup', this.handlePointerUp);
        });

        this.elements.previousButton?.removeEventListener('click', this.handlePrevClick);
        this.elements.nextButton?.removeEventListener('click', this.handleNextClick);
      }

      handlePointerUp(event) {
        event.stopPropagation();
      }

      handleKeydown(event) {
        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.showPreviousMedia();
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.showNextMedia();
        }
      }

      handleTouchStart(event) {
        if (
          event.touches.length !== 1 ||
          event.target.closest('button, a, iframe, model-viewer, video, .shopify-model-viewer-ui')
        ) {
          this.touchStartX = null;
          this.touchStartY = null;
          return;
        }

        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }

      handleTouchEnd(event) {
        if (this.touchStartX === null || this.touchStartY === null || !event.changedTouches.length) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

        if (deltaX > 0) {
          this.showPreviousMedia();
        } else {
          this.showNextMedia();
        }
      }

      getActiveMedia() {
        return this.querySelector(`[data-media-id="${this.activeMediaId}"]`) || this.querySelector('.active[data-media-id]');
      }

      showActiveMedia() {
        const activeMedia =
          this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`) || this.mediaItems?.[0];

        if (!activeMedia) return;
        this.setActiveMedia(activeMedia, { behavior: 'auto' });
      }

      setActiveMedia(activeMedia, options = {}) {
        if (!activeMedia || !this.elements?.container) return;

        const { behavior = 'smooth' } = options;
        const previousMedia = this.getActiveMedia();

        this.mediaItems.forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        this.activeMediaId = activeMedia.dataset.mediaId;

        this.animateMediaChange(previousMedia, activeMedia);
        activeMedia.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      animateMediaChange(previousMedia, activeMedia) {
        if (!window.matchMedia('(max-width: 749px)').matches || !activeMedia || previousMedia === activeMedia) return;

        if (this.motionTimeout) window.clearTimeout(this.motionTimeout);

        this.elements.container.classList.remove('is-switching');
        activeMedia.classList.remove('is-media-entering');
        void activeMedia.offsetWidth;

        this.elements.container.classList.add('is-switching');
        activeMedia.classList.add('is-media-entering');

        this.motionTimeout = window.setTimeout(() => {
          this.elements.container.classList.remove('is-switching');
          activeMedia.classList.remove('is-media-entering');
        }, 320);
      }

      showPreviousMedia() {
        if (!this.mediaItems?.length || this.mediaItems.length < 2) return;

        const currentIndex = this.mediaItems.findIndex((element) => element.dataset.mediaId === this.activeMediaId);
        const nextIndex = currentIndex <= 0 ? this.mediaItems.length - 1 : currentIndex - 1;
        this.setActiveMedia(this.mediaItems[nextIndex]);
      }

      showNextMedia() {
        if (!this.mediaItems?.length || this.mediaItems.length < 2) return;

        const currentIndex = this.mediaItems.findIndex((element) => element.dataset.mediaId === this.activeMediaId);
        const nextIndex = currentIndex === -1 || currentIndex >= this.mediaItems.length - 1 ? 0 : currentIndex + 1;
        this.setActiveMedia(this.mediaItems[nextIndex]);
      }
    }
  );
}
